<template>
  <div class="doppler-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$f' = f [v / (v ± vs)]$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-wave"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 背景装饰 -->
          <rect width="400" height="240" fill="#020617" />
          <line x1="0" y1="120" x2="400" y2="120" stroke="#1e293b" stroke-width="1" stroke-dasharray="4,4" />

          <!-- 波纹群 (Wavefronts) -->
          <g v-for="w in wavefronts" :key="w.id">
             <circle :cx="w.originX" :cy="120" :r="w.r" 
                     fill="none" :stroke="getWaveColor(w)" 
                     :stroke-width="1.8" :opacity="w.opacity" filter="url(#glow-wave)" />
          </g>

          <!-- 运动波源 -->
          <g :transform="`translate(${sourceX}, 120)`">
             <circle r="7" fill="#f43f5e" />
             <!-- 运动矢量 -->
             <line x1="0" y1="0" :x2="sourceV * 8" y2="0" stroke="#f43f5e" stroke-width="2" marker-end="url(#arr-v)" />
             <text y="-12" text-anchor="middle" fill="#f43f5e" font-size="10" font-weight="900">波源 S</text>
          </g>

          <!-- 观察者 B (右岸) -->
          <g transform="translate(360, 120)">
             <circle r="6" fill="#10b981" />
             <text y="22" text-anchor="middle" fill="#10b981" font-size="10" font-weight="bold">观察者 (B)</text>
          </g>

          <defs>
             <marker id="arr-v" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                <path d="M0,0 L6,2 L0,4 Z" fill="#f43f5e" />
             </marker>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan" v-if="sourceV > 0">
              <label>前方观测状态 (B)</label>
              <span class="val" style="color:#38bdf8">频率升高 (蓝移)</span>
           </div>
           <div class="hud-item orange" v-if="sourceV != 0">
              <label>相对波长变化</label>
              <span class="val">{{ (1 - sourceV/10).toFixed(2) }} λ₀</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">波源运动速度 (Vs)</label>
                 <el-slider v-model="sourceV" :min="-8" :max="8" :step="0.5" size="small"></el-slider>
              </div>
              <el-button type="primary" size="mini" plain @click="sourceV = 0">静止参考项</el-button>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-odometer"></i>
           <p><strong>核心揭示：</strong>观察波阵面的分布！当波源向右运动时，<strong>右侧波峰被“挤压”密集</strong>（频率升高），而左侧波峰被拉开变稀（频率降低）。这就是救护车驶近时音调变高，远去时音调变沉的原因。当天体远离我们时，这种现象被称为<strong>红移</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DopplerEffectViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      sourceX: 100,
      sourceV: 3,
      wavefronts: [],
      nextId: 0,
      running: true,
      lastEmittedT: 0
    }
  },
  methods: {
    getWaveColor(w) {
       // Visual hint for shift: Blue if meeting, Red if trailing
       return w.originX < this.sourceX ? '#f43f5e' : '#0ea5e9';
    },
    animate() {
       if (!this.running) return;
       
       const now = performance.now();
       // Wave emission logic
       if (now - this.lastEmittedT > 400) {
          this.wavefronts.push({
             id: this.nextId++,
             originX: this.sourceX,
             r: 0,
             opacity: 0.8
          });
          this.lastEmittedT = now;
       }

       // Update source
       this.sourceX += this.sourceV * 0.4;
       if (this.sourceX > 320) this.sourceX = 80;
       if (this.sourceX < 80) this.sourceX = 320;

       // Update waves
       this.wavefronts.forEach(w => {
          w.r += 2.0; // Wave speed in medium is constant (v)
          w.opacity -= 0.004;
       });
       this.wavefronts = this.wavefronts.filter(w => w.opacity > 0);

       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { 
     this.running = false;
     cancelAnimationFrame(this.animFrame); 
  }
}
</script>

<style scoped lang="scss">
.doppler-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f97316; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f0fdfa; padding: 10px 14px; border-radius: 10px; border: 1px solid #ccfbf1; display: flex; align-items: center; gap: 10px; i { color: #0d9488; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #115e59; line-height: 1.5; strong { color: #134e4a; } } }
</style>
